import { ArrowLeftOutlined, CopyOutlined } from "@ant-design/icons"
import { Alert, Button, Card, Skeleton, Table, Typography, message } from "antd"
import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { compareOrderResultJsons, OrderResultJsonDifference } from "../../endpoints"
import { ShowMessagePage } from "../../config"

const formatValue = (value: unknown): string => {
    if (value === undefined) return "undefined"
    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const formatCopyValue = (value: unknown): string => {
    if (value === undefined) return "undefined"
    if (typeof value === "string") return value
    try {
        return JSON.stringify(value, null, 2)
    } catch {
        return String(value)
    }
}

const normalizeHomopolymers = (sequence: string): string => {
    return sequence.replace(/([ACGT])\1{4,}/gi, (match) => match[0].repeat(3))
}

const toSequence = (value: unknown): string | null => {
    if (typeof value !== "string") return null
    const normalized = normalizeHomopolymers(value.replace(/\s+/g, "").toUpperCase())
    if (normalized.length < 20) return null
    if (!/^[ACGTN]+$/.test(normalized)) return null
    return normalized
}

const reverseComplement = (sequence: string): string => {
    const complements: Record<string, string> = {
        A: "T",
        C: "G",
        G: "C",
        T: "A",
        N: "N",
    }

    let output = ""
    for (let index = sequence.length - 1; index >= 0; index--) {
        const char = sequence[index]
        output += complements[char] || "N"
    }
    return output
}

const isCircularlyIdentical = (reference: string, candidate: string): boolean => {
    if (reference.length !== candidate.length) return false
    return (reference + reference).includes(candidate)
}

export function CompareOrderResults(): JSX.Element {
    const navigate = useNavigate()
    const { orderId } = useParams()
    const [searchParams] = useSearchParams()
    const [messageApi, contextHolder] = message.useMessage()

    const selectedIds = useMemo(() => {
        const raw = searchParams.get("ids") || ""
        return raw
            .split(",")
            .map((id) => Number(id.trim()))
            .filter((id) => Number.isInteger(id) && id > 0)
    }, [searchParams])

    const uniqueIds = useMemo(() => Array.from(new Set(selectedIds)), [selectedIds])

    const { data, isPending, isError, error } = useQuery({
        queryKey: ["compareOrderResultJsons", uniqueIds],
        queryFn: () => compareOrderResultJsons({ orderResultIds: uniqueIds }),
        enabled: uniqueIds.length >= 2,
        refetchOnMount: "always",
    })

    if (uniqueIds.length < 2) {
        return (
            <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
                {contextHolder}
                <Card>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/admin/order/${orderId}`)}>
                        Back
                    </Button>
                    <div style={{ marginTop: 16 }}>
                        <Alert
                            type="warning"
                            message="Select at least 2 results to compare"
                            showIcon
                        />
                    </div>
                </Card>
            </div>
        )
    }

    if (isPending) {
        return (
            <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
                {contextHolder}
                <Skeleton active paragraph={{ rows: 8 }} />
            </div>
        )
    }

    if (isError) {
        return ShowMessagePage("Error", error?.message ?? "Failed to compare results")
    }

    if (!data || data.code !== 1000) {
        return ShowMessagePage("Error", data?.message ?? "Failed to compare results")
    }

    const copyText = async (valueText: string) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(valueText)
            } else {
                const textArea = document.createElement("textarea")
                textArea.value = valueText
                document.body.appendChild(textArea)
                textArea.select()
                document.execCommand("copy")
                document.body.removeChild(textArea)
            }
            messageApi.success("Copied")
        } catch (error) {
            console.error("Copy failed:", error)
            messageApi.error("Failed to copy")
        }
    }

    const dynamicColumns = data.compared.map((item) => ({
        title: `${item.jsonfile} (#${item.orderResultId})`,
        dataIndex: `value_${item.orderResultId}`,
        key: `value_${item.orderResultId}`,
        width: 320,
        render: (value: unknown) => {
            const valueText = formatValue(value)
            const copyValueText = formatCopyValue(value)
            const isLongText = valueText.length > 400

            return (
                <div>
                    {isLongText && (
                        <Button
                            type="link"
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                                copyText(copyValueText)
                            }}
                            style={{ padding: 0, marginBottom: 4 }}
                        >
                            Copy
                        </Button>
                    )}
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 220, overflow: "auto" }}>
                        {valueText}
                    </pre>
                </div>
            )
        },
    }))

    const columns = [
        {
            title: "JSON Path",
            dataIndex: "path",
            key: "path",
            fixed: "left" as const,
            width: 280,
            render: (value: string) => <Typography.Text code>{value}</Typography.Text>,
        },
        {
            title: "Seq Check",
            dataIndex: "sequence_check",
            key: "sequence_check",
            width: 180,
            render: (value: string | undefined) => value ? <Typography.Text type="success">{value}</Typography.Text> : "-",
        },
        ...dynamicColumns,
    ]

    const dataSource = data.differences.map((diff: OrderResultJsonDifference, index: number) => {
        const row: Record<string, unknown> = {
            key: `${diff.path}_${index}`,
            path: diff.path,
        }

        if (diff.values.length === 2) {
            const firstSequence = toSequence(diff.values[0]?.value)
            const secondSequence = toSequence(diff.values[1]?.value)

            if (firstSequence && secondSequence) {
                const rcSecond = reverseComplement(secondSequence)
                if (isCircularlyIdentical(firstSequence, rcSecond)) {
                    row.sequence_check = "RC circular match"
                }
            }
        }

        diff.values.forEach((valueItem) => {
            row[`value_${valueItem.orderResultId}`] = valueItem.value
        })

        return row
    })

    return (
        <div style={{ padding: "24px", maxWidth: "1500px", margin: "0 auto" }}>
            {contextHolder}
            <Card
                title={`Compare Results for Order ${orderId}`}
                extra={
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/admin/order/${orderId}`)}>
                        Back to Order
                    </Button>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Typography.Text>
                        Compared {data.compared.length} JSON file(s). Showing up to {data.maxDifferences} differences.
                    </Typography.Text>
                </div>

                <div style={{ marginBottom: 16 }}>
                    {data.compared.map((item) => (
                        <div key={`barcode-paths-${item.orderResultId}`} style={{ marginBottom: 8 }}>
                            <Typography.Text strong>
                                {item.jsonfile} (#{item.orderResultId}) sample to barcode mapping:
                            </Typography.Text>
                            <pre style={{ margin: "4px 0 0 0", whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 140, overflow: "auto" }}>
                                {item.sampleMappings && item.sampleMappings.length > 0
                                    ? item.sampleMappings
                                        .map((mapping) => `samples[${mapping.sampleIndex}] sampleId=${mapping.sampleId ?? "-"} barcodePath=${mapping.barcodePath || "(empty)"} sampleName=${mapping.sampleName || "-"}`)
                                        .join("\n")
                                    : "(none)"}
                            </pre>
                        </div>
                    ))}
                </div>

                {data.truncated && (
                    <Alert
                        type="info"
                        showIcon
                        message={`Only the first ${data.maxDifferences} differences are shown to reduce processing time and network payload.`}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {!data.truncated && data.differences.length === 0 && (
                    <Alert
                        type="success"
                        showIcon
                        message="No differences found. All keys and values match across selected JSON files."
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Table
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    size="small"
                    scroll={{ x: "max-content" }}
                    locale={{ emptyText: "No differences found" }}
                />
            </Card>
        </div>
    )
}
